import { pickBlogLocalized, type BlogPostRecord } from "@/lib/blog-cards";
import { getBlogPageCopy } from "@/lib/blog-page-copy";
import type { Lang } from "@/lib/types";

/** Average reading pace used by the legacy Nuxt blog sync (words per minute). */
export const BLOG_READ_WORDS_PER_MINUTE = 238;

export function stripHtmlToPlainText(html: string): string {
  return html.replace(/<[^>]*>?/g, " ").replace(/\s+/g, " ").trim();
}

export function countWords(plainText: string): number {
  if (!plainText) return 0;
  return plainText.split(/\s+/).filter(Boolean).length;
}

export function computeReadTimeMinutesFromHtml(html: string): number {
  const words = countWords(stripHtmlToPlainText(html));
  return Math.ceil(words / BLOG_READ_WORDS_PER_MINUTE);
}

/** Primary post body for read-time (matches Nuxt: base `content`, not per-locale). */
export function htmlForReadTime(post: BlogPostRecord): string {
  const content = post.content;
  if (content) {
    if (typeof content === "string") return content;
    if (typeof content.en === "string" && content.en) return content.en;
    const localized = pickBlogLocalized(content, "en");
    if (localized) return localized;
  }
  const intro = pickBlogLocalized(post.intro, "en") || pickBlogLocalized(post.description, "en");
  return intro;
}

export function resolveReadTimeMinutes(post: BlogPostRecord): number {
  if (typeof post.readTime === "number" && post.readTime > 0) {
    return post.readTime;
  }
  return computeReadTimeMinutesFromHtml(htmlForReadTime(post));
}

export function formatBlogMinRead(minutes: number, lang: Lang): string {
  return `${minutes} ${getBlogPageCopy(lang).readTime.minRead}`;
}

export function getBlogReadTimeLabel(lang: Lang): string {
  return getBlogPageCopy(lang).readTime.label;
}

export function formatBlogReadTimeForPost(post: BlogPostRecord, lang: Lang): string | null {
  const minutes = resolveReadTimeMinutes(post);
  if (minutes <= 0) return null;
  return formatBlogMinRead(minutes, lang);
}
